import {
  useMemo,
  useCallback,
  useState,
  useEffect,
  createElement,
  useRef,
} from 'react';
import anime from 'animejs/lib/anime.es.js';

import styles from './SlotMachineGameLetters.module.css';
import PullSong from 'assets/games/theme-two/handle_pull.mp3';
import ScoreSong from 'assets/games/theme-two/Score.mp3';
import LetterSong from 'assets/games/theme-two/letter.mp3';
import applauseSong from 'assets/games/theme-two/applause.mp3';
import rollFinishSong from 'assets/games/theme-two/roll_finish.mp3';
import song from 'assets/games/theme-two/slotBgSound.mp3';

export default function SlotMachineGameLetters({
  fetchData,
  winCondition,
  currentWinnerIndex,
  setIsWinnerGenerated,
  setCurrentWinnerIndex,
  setWinCondition,
  direction = 'down',
  totalPrizes = 0,
}) {
  let winnerColumns = 0;
  let block = 0;
  let callBeckToStartText1Time = true;
  let callResetGame1Time = true;
  let colorBlocker = 0;

  const spinnerPaths = require.context(
    'assets/games/theme-two/slot/spinner',
    false,
    /\.png$/
  );
  const spinnerImagesPaths = spinnerPaths
    .keys()
    .filter((key) => key.startsWith('./'))
    .map(spinnerPaths);

  const [html, setHtml] = useState();
  const [spinnerImage, setSpinnerImage] = useState(spinnerImagesPaths[0]);
  const [isAlreadySpin, setIsAlreadySpin] = useState(false);
  const [isStartAllowed, setIsStartAllowed] = useState(true);
  const [handlePullAudio] = useState(new Audio(PullSong));
  const [scoreAudio] = useState(new Audio(ScoreSong));
  const [letterAudio] = useState(new Audio(LetterSong));
  const [applauseAudio] = useState(new Audio(applauseSong));
  const [rollFinishAudio] = useState(new Audio(rollFinishSong));
  const [bgAudio] = useState(new Audio(song));

  const preloadedImages = useRef([]);

  useEffect(() => {
    preloadedImages.current = spinnerImagesPaths.map((src) => {
      const img = new Image();
      img.src = src;
      return img;
    });
  }, []);

  const numColumns = useMemo(() => {
    return 12;
  }, []);
  const numRows = useMemo(() => {
    return 120;
  }, []);
  const characters = useMemo(() => {
    return [
      'A',
      'B',
      'C',
      'D',
      'E',
      'F',
      'G',
      'H',
      'I',
      'J',
      'K',
      'L',
      'M',
      'N',
      'O',
      'P',
      'Q',
      'R',
      'S',
      'T',
      'U',
      'V',
      'W',
      'X',
      'Y',
      'Z',
    ];
  }, []);

  const START_TEXT = useMemo(() => {
    return [' ', ' ', 'L', 'M', 'C', 'T', ' ', 'P', 'L', 'U', 'S', ' '];
  }, []);

  const initialState = useCallback(() => {
    setSpinnerImage(spinnerImagesPaths[0]);
    callBeckToStartText1Time = true;
    callResetGame1Time = true;
    colorBlocker = 0;
    const slotGameMachine = document.getElementById('slotGameMachineContainer');
    slotGameMachine.classList.remove('started');
    setIsAlreadySpin(false);
    setIsWinnerGenerated((prevState) => {
      return prevState === 1 ? 0 : prevState;
    });

    handlePullAudio.pause();
    handlePullAudio.currentTime = 0;
    applauseAudio.pause();
    applauseAudio.currentTime = 0;
    letterAudio.pause();
    letterAudio.currentTime = 0;
    rollFinishAudio.pause();
    rollFinishAudio.currentTime = 0;
    scoreAudio.currentTime = 0;

    const tempHtml = [...Array(numColumns)].map((_, column) => {
      let itemClass = '';
      if (column % 2 === 0) {
        itemClass = 'even';
      } else {
        itemClass = 'odd';
      }
      return (
        <div
          key={column}
          className={`${styles.slotGameMachineColumn1} col${column} ${itemClass}`}
          id={`col${column}`}
        >
          {[...Array(numRows)].map((_, row) => {
            let char =
              characters[Math.floor(Math.random() * characters.length)];
            let color = null;

            if (column % 2 === 0 && row === 1) {
              char = START_TEXT[column];
              color = 'white';
            }

            if (column % 2 !== 0 && row === 118) {
              char = START_TEXT[column];
              color = 'white';
            }

            return (
              <h2
                key={row}
                className={`${styles.slotGameMachineRow} number-${row}`}
                style={{ color: color }}
              >
                {char}
              </h2>
            );
          })}
        </div>
      );
    });
    setHtml(tempHtml);
  }, [START_TEXT, characters, numColumns, numRows]);

  const start = useCallback(() => {
    const isFinished =
      direction === 'up'
        ? currentWinnerIndex >= totalPrizes
        : currentWinnerIndex < 0;
    if (isFinished) {
      return;
    }
    const startSpinner = document.getElementById('startSpinner');
    const slotGameMachine = document.getElementById('slotGameMachineContainer');
    if (slotGameMachine.classList.contains('started')) {
      return;
    }
    slotGameMachine.classList.add('started');
    handlePullAudio.play();

    let index = 0;

    async function animate() {
      const img = preloadedImages.current[index];
      setSpinnerImage(img.src);
      index++;
      if (index < preloadedImages.current.length) {
        console.log('1');
        requestAnimationFrame(animate);
      } else {
        console.log('2');
        fetchData();
      }
    }

    animate();
    startSpinner.removeEventListener('click', start);
  }, [spinnerImagesPaths]);

  useEffect(() => {
    initialState();
    function handleKeyDown(e) {
      if (e.keyCode >= 65 && e.keyCode <= 90) {
        if (bgAudio.muted) {
          bgAudio.play();
          bgAudio.volume = 0.4;
          bgAudio.muted = false;
        } else {
          bgAudio.muted = true;
        }
      }
      if (e.keyCode === 32) {
        start();
      }
    }

    document.addEventListener('keydown', handleKeyDown);

    // Don't forget to clean up
    return function cleanup() {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [initialState, currentWinnerIndex]);

  const beckToStartText = useCallback(() => {
    const row22 = document.getElementsByClassName('number-22');
    const row58 = document.getElementsByClassName('number-58');

    for (let i = 0; i < numColumns; i++) {
      for (let j = 0; j < numRows; j++) {
        if (i % 2 === 0 && j === 22) {
          row22[i].textContent = START_TEXT[i];
          row22[i].style.color = 'white';
        }

        if (i % 2 !== 0 && j === 58) {
          row58[i].textContent = START_TEXT[i];
          row58[i].style.color = 'white';
        }
      }
    }
  }, [START_TEXT, numColumns, numRows]);

  const resetSpine = useCallback((position, target, func) => {
    anime({
      targets: target,
      top: position,
      easing: 'easeInOutCubic',
      duration: 7000,
      update: function (anim) {
        if (anim.progress >= 10 && callBeckToStartText1Time) {
          callBeckToStartText1Time = false;

          beckToStartText();
        }
        if (func) {
          func(anim);
        }
      },
      complete: function () {
        if (callResetGame1Time) {
          callResetGame1Time = false;
          // const bgAudio = document.getElementById('bgAudio');
          // if (bgAudio) {
          bgAudio.play();
          // }
          setTimeout(() => {
            setWinCondition(null);
            setCurrentWinnerIndex((prevValue) => {
              return direction === 'up' ? prevValue + 1 : prevValue - 1;
            });
            initialState();
          }, 2000);
        }
      },
    });
  }, []);

  const resetAnimGoToStart = useCallback(() => {
    let del = 1200;
    for (let i = 0; i < numColumns; i++) {
      if (i === 0) {
        setTimeout(() => {
          resetSpine(3565, `#colNum${i}`);
        }, del);
        del -= 100;
      }
      if (i % 2 === 0 && i !== 0) {
        setTimeout(() => {
          resetSpine(3565, `#colNum${i}`);
        }, del);
      }
      del -= 100;
      if (i % 2 !== 0) {
        setTimeout(() => {
          resetSpine(-35, `#colNum${i}`);
        }, del);
      }
    }
  }, []);

  const stopColumnAnim = useCallback(
    (target, createdColumns) => {
      anime({
        targets: target,
        // top: `${2050}px`,
        top: `${1762}px`,
        duration: 700,
        easing: 'cubicBezier(0.140, 0.435, 0.780, 1.385)',
        complete: function () {
          letterAudio.currentTime = 0;
          letterAudio.play();
          if (createdColumns === numColumns - 1) {
            scoreAudio.pause();
            rollFinishAudio.play();
            // if (bgAudio) {
            bgAudio.pause();
            // }
            applauseAudio.play();

            setIsWinnerGenerated(1);
            setTimeout(() => {
              resetAnimGoToStart();
            }, 5000);
          }
        },
      });
    },
    [
      currentWinnerIndex,
      numColumns,
      setCurrentWinnerIndex,
      setIsWinnerGenerated,
    ]
  );

  const winAnim = useCallback(() => {
    setTimeout(
      async () => {
        if (winnerColumns < numColumns) {
          const innerNumRows = numRows / 2;
          const childs = [];

          for (let j = 0; j < innerNumRows; j++) {
            let char =
              characters[Math.floor(Math.random() * characters.length)];
            let color = null;
            if (j === 40) {
              color = 'white';
              char = winCondition[winnerColumns];
            }
            childs.push(
              createElement(
                'h2',
                {
                  className: `${styles.slotGameMachineRow} number-${j}`,
                  key: `${winnerColumns}-${j}`,
                  style: { color: color || '' },
                },
                char
              )
            );
          }
          const column2 = createElement(
            'div',
            {
              className: `${styles.slotGameMachineColumn2} colNum${winnerColumns}`,
              id: `colNum${winnerColumns}`,
            },
            childs
          );
          setHtml((prevState) => {
            return prevState.map((item, i) => {
              return winnerColumns === i ? column2 : item;
            });
          });
          stopColumnAnim(`#colNum${winnerColumns}`, winnerColumns);
          winnerColumns++;
          winAnim();
        }
      },
      winnerColumns === 0 ? 1000 : 500
    );
  }, [numColumns, numRows, html, characters, winCondition, setHtml]);

  const updateAnim1 = useCallback(
    (anim) => {
      if (colorBlocker === 0) {
        setTimeout(() => {
          document.querySelectorAll('.number-118').forEach((el) => {
            el.style.color = 'rgba(255, 255, 255, 23%)';
          });
          document.querySelectorAll('.number-1').forEach((el) => {
            el.style.color = 'rgba(255, 255, 255, 23%)';
          });
        }, 500);
      }
      let loops1 = 0;

      if (anim.progress >= 40) {
        // clearAnimTimeouts();

        document
          .querySelectorAll(`.${styles.slotGameMachineColumn1}`)
          .forEach((el) => {
            anime.remove(el);
          });

        anime({
          targets: '.even',
          top: [
            { value: '0px', duration: 0, delay: 0 },
            { value: '-2200px', duration: 500, delay: 0 },
          ],
          easing: 'linear',
          loop: true,
          loopComplete: function () {
            loops1++;
          },
        });

        anime({
          targets: '.odd',
          top: [
            { value: '0px', duration: 0, delay: 0 },
            { value: '2200px', duration: 500, delay: 0 },
          ],
          easing: 'linear',
          loop: true,
          loopComplete: function () {
            loops1++;
          },
          update: function () {
            if (winCondition && loops1 === 6 && block === 0) {
              block++;
              winAnim();
            }
          },
        });
      }
    },
    [winAnim, winCondition]
  );

  const spin = useCallback(() => {
    if (isAlreadySpin) {
      return;
    }
    setIsAlreadySpin(true);
    scoreAudio.play();
    let del = 1200;

    for (let i = 0; i < numColumns; i++) {
      if (i === 0) {
        setTimeout(() => {
          anime({
            targets: `#col${i}`,
            top: '-13650px',
            easing: 'easeInQuad',
            duration: 4000,
            update: (anim) => {
              updateAnim1(anim);
            },
          });
        }, del);
      }
      del -= 100;
      if (i % 2 === 0 && i !== 0) {
        setTimeout(() => {
          anime({
            targets: `#col${i}`,
            top: '-13650px',
            easing: 'easeInQuad',
            duration: 4000,
          });
        }, del);
      }

      if (i % 2 !== 0) {
        setTimeout(() => {
          anime({
            targets: `#col${i}`,
            top: '13650px',
            easing: 'easeInQuad',
            duration: 4000,
          });
        }, del);
      }
    }
  }, [numColumns, scoreAudio, updateAnim1]);

  useEffect(() => {
    if (!winCondition) {
      return;
    }
    spin();
  }, [winCondition]);

  return (
    <>
      <div className={styles.SlotGameMachineWrapperContainer}>
        <div
          className={styles.SlotGameMachineContainer}
          id="slotGameMachineContainer"
        >
          <div className={styles.slotGameMachineColumnContainer}>
            <div className={styles.slotGameMachineColumnContainer1}>{html}</div>
          </div>
        </div>
      </div>
      <div
        className={styles.slotGameSpinnerWrapper}
        id="slotGameMachineSpinnerContainer"
        onClick={() => {
          start();
        }}
      >
        <img src={spinnerImage} id="startSpinner" alt="spinner" />
      </div>
    </>
  );
}
